import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { useReviewStats, useReviewAttention } from '@/hooks/use-reviews'
import { KpiStrip } from '@/components/reviews/kpi-strip'
import { StarDistribution } from '@/components/reviews/star-distribution'
import { AttentionTodo } from '@/components/reviews/attention-todo'
import { PropertiesTable } from '@/components/reviews/properties-table'
import { SubratingHeatmap } from '@/components/reviews/subrating-heatmap'
import { SubratingShortfall } from '@/components/reviews/subrating-shortfall'
import { SilentStays } from '@/components/reviews/silent-stays'
import { SubFiveTrend } from '@/components/reviews/sub-five-trend'
import { MonthlyChart } from '@/components/reviews/monthly-chart'
import { CalibrationPanel } from '@/components/review-chase/calibration-panel'
import { ReviewChasePage } from '@/pages/review-chase'

/**
 * The Reviews hub: how the portfolio is doing on reviews (this year
 * against last), what guests are asking to be fixed, which properties
 * lose stars and where, today's chase, and the evidence behind the
 * confidence score. Every widget reads a nightly summary; nothing here
 * recomputes on load.
 */
export function ReviewsPage() {
  const stats = useReviewStats()
  const attention = useReviewAttention()
  const openAttention = attention.data?.items.filter(i => i.status === 'open').length ?? 0

  return (
    <div className="space-y-6">
      {stats.isLoading ? (
        <LoadingSkeleton className="h-24 w-full" />
      ) : stats.isError ? (
        <ErrorState error={stats.error} onRetry={() => stats.refetch()} />
      ) : !stats.data?.stats ? (
        <Card>
          <CardHeader>
            <CardTitle>No review statistics yet</CardTitle>
            <CardDescription>The first nightly run (21:00 UTC) writes them.</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          <KpiStrip stats={stats.data.stats} openAttention={openAttention} />
          <StarDistribution stats={stats.data.stats} />
        </>
      )}

      {/* The chase sections are cards of their own (Checking out today, Pending reviews). */}
      <ReviewChasePage embedded />

      <AttentionTodo />

      {stats.data?.stats && (
        <>
          <PropertiesTable stats={stats.data.stats} />
          <div className="grid gap-6 xl:grid-cols-2">
            <MonthlyChart stats={stats.data.stats} />
            <SubFiveTrend stats={stats.data.stats} />
          </div>
          <div className="grid gap-6 xl:grid-cols-2">
            <SubratingShortfall stats={stats.data.stats} />
            <SilentStays stats={stats.data.stats} />
          </div>
          {/*
            Full width, not half: seven columns in a half-width card pushed
            Communication off the right edge behind a horizontal scroll, so
            one of the six sub-ratings was simply unreadable.
          */}
          <SubratingHeatmap stats={stats.data.stats} />
        </>
      )}

      <CalibrationPanel />
    </div>
  )
}
